import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProfile, generateWorkoutPlan } from '../services/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import BodyMap from '../components/BodyMap';
import { Loader2, Zap, Clock, Target, AlertCircle, Dumbbell, Heart, Home, Building2, Lock, Crown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('cardioai_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const GeneratePlanPage = () => {
  const navigate = useNavigate();
  const { user, userPlan } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [profile, setProfile] = useState(null);
  const [workoutTypes, setWorkoutTypes] = useState({});
  const [muscleGroups, setMuscleGroups] = useState({});
  const [cardioTypes, setCardioTypes] = useState({});
  const [showPaywall, setShowPaywall] = useState(false);
  const [planLimits, setPlanLimits] = useState(null);
  const [formData, setFormData] = useState({
    duration_weeks: '4',
    focus_area: '',
    workout_type: 'strength',
    training_location: 'both',
    muscle_groups: ['full_body'],
    cardio_types: [],
    include_videos: true
  });

  // Fetch plan limits
  useEffect(() => {
    const fetchLimits = async () => {
      try {
        const response = await axios.get(`${API}/user/plan-limits`, {
          headers: getAuthHeaders()
        });
        setPlanLimits(response.data);
      } catch (error) {
        console.error('Error fetching plan limits:', error);
      }
    };
    fetchLimits();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileData, typesRes, muscleRes, cardioRes] = await Promise.all([
          getProfile(),
          axios.get(`${API}/workout-types`),
          axios.get(`${API}/muscle-groups`),
          axios.get(`${API}/cardio-types`)
        ]);
        
        setProfile(profileData);
        setWorkoutTypes(typesRes.data);
        setMuscleGroups(muscleRes.data);
        setCardioTypes(cardioRes.data);
        
        if (!profileData) {
          toast.error('Completa o teu perfil antes de gerar um plano');
          navigate('/profile');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(t('errorLoading'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    // Paywall temporarily disabled
    // if (userPlan === 'free') {
    //   setShowPaywall(true);
    //   return;
    // }
    
    setGenerating(true);
    
    try {
      const result = await generateWorkoutPlan({
        duration_weeks: parseInt(formData.duration_weeks),
        focus_area: formData.focus_area || null,
        workout_type: formData.workout_type,
        training_location: formData.training_location,
        muscle_groups: formData.workout_type === 'strength' ? formData.muscle_groups : null,
        cardio_types: formData.cardio_types.length > 0 ? formData.cardio_types : null,
        include_videos: formData.include_videos
      });
      
      toast.success(t('planGenerated'));
      navigate(`/history/${result.id}`);
    } catch (error) {
      console.error('Error generating plan:', error);
      toast.error(error.response?.data?.detail || t('errorGenerating'));
    } finally {
      setGenerating(false);
    }
  };

  const toggleMuscleGroup = (key) => {
    setFormData(prev => {
      const current = prev.muscle_groups;
      
      // If selecting "full_body", clear others
      if (key === 'full_body') {
        return { ...prev, muscle_groups: ['full_body'] };
      }
      
      // If selecting specific group, remove "full_body"
      let newGroups = current.filter(g => g !== 'full_body');
      
      if (newGroups.includes(key)) {
        newGroups = newGroups.filter(g => g !== key);
      } else {
        newGroups = [...newGroups, key];
      }
      
      // If no groups selected, default to full_body
      if (newGroups.length === 0) {
        newGroups = ['full_body'];
      }
      
      return { ...prev, muscle_groups: newGroups };
    });
  };

  const experienceLevels = {
    beginner: t('beginner'),
    intermediate: t('intermediate'),
    advanced: t('advanced')
  };

  const locationIcons = {
    home: <Home className="w-5 h-5" />,
    gym: <Building2 className="w-5 h-5" />,
    both: <Dumbbell className="w-5 h-5" />
  };

  const locationLabels = {
    home: t('home'),
    gym: t('gym'),
    both: t('both')
  };

  const workoutTypeIcons = {
    cardio: <Heart className="w-5 h-5 text-red-400" />,
    strength: <Dumbbell className="w-5 h-5 text-blue-400" />,
    hiit: <Zap className="w-5 h-5 text-yellow-400" />,
    circuit: <Target className="w-5 h-5 text-cyan-400" />
  };

  // Custom SVG icons for muscle groups representing body parts
  const MuscleIcon = ({ type, className = "w-5 h-5" }) => {
    const icons = {
      full_body: (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <circle cx="12" cy="4" r="2.5"/>
          <path d="M12 7.5c-2 0-3.5 1-4 2.5l-1.5 5h2l1 3.5h5l1-3.5h2l-1.5-5c-.5-1.5-2-2.5-4-2.5z"/>
          <path d="M8.5 18.5l-1 3h2l.5-3h-1.5zM15.5 18.5l1 3h-2l-.5-3h1.5z"/>
        </svg>
      ),
      chest: (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M12 6c-4 0-7 2-7 4v4c0 1.5 1.5 3 4 3.5v-2c-1-.3-2-.8-2-1.5v-4c0-1 2-2.5 5-2.5s5 1.5 5 2.5v4c0 .7-1 1.2-2 1.5v2c2.5-.5 4-2 4-3.5v-4c0-2-3-4-7-4z"/>
          <path d="M10 12c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2z"/>
        </svg>
      ),
      back: (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M12 4c-3 0-5.5 1.5-6 4l-1 6c0 2 1.5 3.5 3.5 4v-2c-1-.3-1.5-1-1.5-2l1-6c.3-1.5 2-2.5 4-2.5s3.7 1 4 2.5l1 6c0 1-.5 1.7-1.5 2v2c2-.5 3.5-2 3.5-4l-1-6c-.5-2.5-3-4-6-4z"/>
          <path d="M12 10v8M9 12h6"/>
        </svg>
      ),
      shoulders: (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <circle cx="12" cy="5" r="2"/>
          <path d="M6 9c0-1 1.5-2 3-2h6c1.5 0 3 1 3 2l1 4h-2l-1-3h-2v5h-4v-5h-2l-1 3h-2l1-4z"/>
          <circle cx="6" cy="10" r="1.5"/>
          <circle cx="18" cy="10" r="1.5"/>
        </svg>
      ),
      arms: (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M7 6c-1 0-2 .5-2 1.5v4c0 1.5.5 2.5 1.5 3l1.5 1v4h3v-5l-2-1.5v-5c0-.5-.5-1-1-1h-1z"/>
          <path d="M17 6c1 0 2 .5 2 1.5v4c0 1.5-.5 2.5-1.5 3l-1.5 1v4h-3v-5l2-1.5v-5c0-.5.5-1 1-1h1z"/>
          <circle cx="6" cy="8" r="2"/>
          <circle cx="18" cy="8" r="2"/>
        </svg>
      ),
      legs: (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M9 4h2v6l-1 4v6h-2v-6l-1-4v-4c0-1 .5-2 2-2z"/>
          <path d="M15 4h-2v6l1 4v6h2v-6l1-4v-4c0-1-.5-2-2-2z"/>
          <ellipse cx="9" cy="10" rx="1.5" ry="2"/>
          <ellipse cx="15" cy="10" rx="1.5" ry="2"/>
        </svg>
      ),
      core: (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M8 8h8v10H8z" opacity="0.3"/>
          <path d="M12 6c-2.5 0-4.5 1-5 2v10c.5 1 2.5 2 5 2s4.5-1 5-2V8c-.5-1-2.5-2-5-2zm3 11h-6v-2h6v2zm0-3h-6v-2h6v2zm0-3h-6V9h6v2z"/>
        </svg>
      ),
      upper_body: (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <circle cx="12" cy="4" r="2"/>
          <path d="M6 8c0-1 2-2 6-2s6 1 6 2v6H6V8z"/>
          <circle cx="5" cy="9" r="1.5"/>
          <circle cx="19" cy="9" r="1.5"/>
          <path d="M4 9v4h2v-4zM18 9v4h2v-4z"/>
        </svg>
      ),
      lower_body: (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M8 6h8v4H8z"/>
          <path d="M9 10h2v10H9zM13 10h2v10h-2z"/>
          <ellipse cx="10" cy="14" rx="1.5" ry="2.5"/>
          <ellipse cx="14" cy="14" rx="1.5" ry="2.5"/>
        </svg>
      )
    };
    return icons[type] || icons.full_body;
  };

  const muscleGroupColors = {
    full_body: 'text-[#32CD32]',
    chest: 'text-purple-400',
    back: 'text-blue-400',
    shoulders: 'text-orange-400',
    arms: 'text-pink-400',
    legs: 'text-red-400',
    core: 'text-yellow-400',
    upper_body: 'text-indigo-400',
    lower_body: 'text-teal-400'
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="generate-plan-page">
        {/* Paywall Modal */}
        {showPaywall && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
            <Card className="w-full max-w-md bg-[#121212] border-[#32CD32]/30">
              <CardContent className="pt-8 pb-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#32CD32]/20 flex items-center justify-center">
                  <Lock className="w-8 h-8 text-[#32CD32]" />
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  {t('premiumFeature')}
                </h2>
                <p className="text-gray-400 mb-6">
                  A geração de planos de treino personalizados está disponível apenas para assinantes Pro ou Premium.
                </p>
                <div className="space-y-3">
                  <Link to="/pricing">
                    <Button 
                      className="w-full btn-primary h-12"
                      data-testid="upgrade-btn"
                    >
                      <Crown className="w-5 h-5 mr-2" />
                      Ver Planos e Fazer Upgrade
                    </Button>
                  </Link>
                  <Button 
                    variant="ghost" 
                    className="w-full text-gray-400"
                    onClick={() => setShowPaywall(false)}
                  >
                    Cancelar
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Com o plano Pro ou Premium, gera planos ilimitados de cardio, musculacao e muito mais!
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="mb-8">
          <h1 
            className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-2"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            {t('generateWord')} <span className="text-[#32CD32]">{t('planWord')}</span>
          </h1>
          <p className="text-gray-400">
            {t('generatePlanDesc')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Summary */}
          <Card className="lg:col-span-1 bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-lg uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                {t('yourProfile')}
              </CardTitle>
              <CardDescription>{t('dataUsedForPersonalization')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {profile && (
                <>
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-[#A1A1AA] text-sm">{t('age')}</span>
                    <span className="font-medium">{profile.age} {t('yearsShort')}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-[#A1A1AA] text-sm">{t('weight')}</span>
                    <span className="font-medium">{profile.weight} kg</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-[#A1A1AA] text-sm">{t('height')}</span>
                    <span className="font-medium">{profile.height} cm</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-[#A1A1AA] text-sm">{t('experienceLevel')}</span>
                    <span className="font-medium text-[#32CD32]">{experienceLevels[profile.experience_level]}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-[#A1A1AA] text-sm">{t('weeklyFrequency')}</span>
                    <span className="font-medium">{profile.weekly_frequency}{t('perWeek')}</span>
                  </div>
                  <div className="pt-2">
                    <span className="text-[#A1A1AA] text-sm block mb-2">{t('goals')}</span>
                    <p className="text-sm">{profile.goals}</p>
                  </div>
                  {profile.injuries && (
                    <div className="pt-2">
                      <span className="text-gray-500 text-sm block mb-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Lesões
                      </span>
                      <p className="text-sm text-orange-400">{profile.injuries}</p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Generation Form */}
          <Card className="lg:col-span-2 bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                <Zap className="w-5 h-5 text-[#32CD32]" />
                {t('configurePlan')}
              </CardTitle>
              <CardDescription>{t('chooseTypeDesc')}</CardDescription>
              
              {/* Plan Limits Info */}
              {planLimits && (
                <div className={`mt-4 p-3 rounded-lg border ${
                  planLimits.can_generate_plan 
                    ? 'bg-[#32CD32]/10 border-[#32CD32]/30' 
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {planLimits.can_generate_plan ? (
                        <Zap className="w-4 h-4 text-[#32CD32]" />
                      ) : (
                        <Lock className="w-4 h-4 text-red-400" />
                      )}
                      <span className="text-sm">
                        {planLimits.monthly_plan_limit === -1 ? (
                          <span className="text-[#32CD32]">{t('unlimitedPlans')}</span>
                        ) : planLimits.can_generate_plan ? (
                          <>
                            <span className="text-white font-medium">{planLimits.plans_remaining}</span>
                            <span className="text-gray-400"> plano(s) restante(s) este mês</span>
                          </>
                        ) : (
                          <span className="text-red-400">{t('monthlyLimitReached')}</span>
                        )}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 capitalize">{t('planLabel')}: {planLimits.plan}</span>
                  </div>
                  {!planLimits.can_generate_plan && (
                    <Button 
                      type="button"
                      onClick={() => navigate('/pricing')}
                      className="w-full mt-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold"
                      size="sm"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Fazer Upgrade
                    </Button>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              <form onSubmit={handleGenerate} className="space-y-6">
                {/* Workout Type Selection */}
                <div className="space-y-3">
                  <Label className="text-gray-300">{t('workoutType')}</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(workoutTypes).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData({ ...formData, workout_type: key })}
                        className={`p-4 rounded-xl border text-center transition-all ${
                          formData.workout_type === key
                            ? 'bg-[#32CD32]/10 border-[#32CD32] text-[#32CD32]'
                            : 'bg-black/30 border-white/10 hover:border-white/30'
                        }`}
                        data-testid={`workout-type-${key}`}
                      >
                        <div className="flex justify-center mb-2">
                          {workoutTypeIcons[key] || <Dumbbell className="w-5 h-5" />}
                        </div>
                        <span className="text-sm font-medium">{t(key) === key ? label : t(key)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Training Location */}
                <div className="space-y-3">
                  <Label className="text-gray-300">{t('trainingLocation')}</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(locationLabels).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData({ ...formData, training_location: key })}
                        className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                          formData.training_location === key
                            ? 'bg-[#32CD32]/10 border-[#32CD32] text-[#32CD32]'
                            : 'bg-black/30 border-white/10 hover:border-white/30'
                        }`}
                        data-testid={`location-${key}`}
                      >
                        {locationIcons[key]}
                        <span className="text-sm font-medium">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Cardio Types (if cardio selected) */}
                {formData.workout_type === 'cardio' && (
                  <div className="space-y-3">
                    <Label className="text-gray-300">{t('cardioTypes')}</Label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {Object.entries(cardioTypes).map(([key, label]) => (
                        <div 
                          key={key}
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                            formData.cardio_types.includes(key)
                              ? 'bg-[#32CD32]/10 border-[#32CD32]/50'
                              : 'bg-black/30 border-white/10 hover:border-white/30'
                          }`}
                          onClick={() => {
                            const current = formData.cardio_types;
                            if (current.includes(key)) {
                              setFormData({ ...formData, cardio_types: current.filter(c => c !== key) });
                            } else {
                              setFormData({ ...formData, cardio_types: [...current, key] });
                            }
                          }}
                        >
                          <Checkbox 
                            checked={formData.cardio_types.includes(key)}
                            className="border-white/30 data-[state=checked]:bg-[#32CD32] data-[state=checked]:border-[#32CD32]"
                          />
                          <span className="text-xs">{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Muscle Groups - Body Map (if strength selected) */}
                {formData.workout_type === 'strength' && (
                  <div className="space-y-3">
                    <Label className="text-gray-300">{t('muscleGroups')}</Label>
                    <p className="text-xs text-gray-500">{t('selectMuscles')}</p>
                    <div className="flex justify-center py-4">
                      <BodyMap 
                        selectedMuscles={formData.muscle_groups}
                        onToggleMuscle={(muscle) => {
                          if (muscle === 'full_body') {
                            setFormData(prev => ({ ...prev, muscle_groups: ['full_body'] }));
                          } else {
                            setFormData(prev => {
                              let newGroups = prev.muscle_groups.filter(g => g !== 'full_body');
                              if (newGroups.includes(muscle)) {
                                newGroups = newGroups.filter(g => g !== muscle);
                              } else {
                                newGroups = [...newGroups, muscle];
                              }
                              if (newGroups.length === 0) {
                                newGroups = ['full_body'];
                              }
                              return { ...prev, muscle_groups: newGroups };
                            });
                          }
                        }}
                        onSelectFullBody={() => {
                          setFormData(prev => ({ ...prev, muscle_groups: ['full_body'] }));
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Focus Area */}
                <div className="space-y-2">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Target className="w-4 h-4" /> Foco Especial (opcional)
                  </Label>
                  <Input
                    placeholder={formData.workout_type === 'cardio' 
                      ? "Ex: Perda de peso, maratona, resistência..." 
                      : "Ex: Hipertrofia, força, definição, peito e costas..."}
                    value={formData.focus_area}
                    onChange={(e) => setFormData({ ...formData, focus_area: e.target.value })}
                    className="h-14 bg-black/50 border-white/20 rounded-xl"
                    data-testid="focus-area-input"
                  />
                </div>

                {/* Include Videos */}
                {formData.workout_type !== 'cardio' && (
                  <div 
                    className="flex items-center gap-3 p-4 rounded-xl bg-black/30 border border-white/10 cursor-pointer"
                    onClick={() => setFormData({ ...formData, include_videos: !formData.include_videos })}
                  >
                    <Checkbox 
                      checked={formData.include_videos}
                      className="border-white/30 data-[state=checked]:bg-[#32CD32] data-[state=checked]:border-[#32CD32]"
                    />
                    <div>
                      <p className="font-medium">Incluir vídeos de demonstração</p>
                      <p className="text-sm text-gray-500">Links para vídeos de cada exercício de musculação</p>
                    </div>
                  </div>
                )}

                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="w-full btn-primary h-16 rounded-xl text-lg"
                    disabled={generating}
                    data-testid="generate-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="mr-2 w-6 h-6 animate-spin" />
                        A Gerar Plano com IA...
                      </>
                    ) : (
                      <>
                        <Zap className="mr-2 w-6 h-6" />
                        Gerar Plano de Treino
                      </>
                    )}
                  </Button>
                </div>

                {generating && (
                  <div className="text-center text-sm text-gray-500 animate-pulse">
                    A IA está a criar um plano personalizado com exercícios e vídeos de demonstração...
                    <br />
                    Isto pode demorar alguns segundos.
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default GeneratePlanPage;
